import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import { openProductQuoteOnWhatsApp } from '@/utils/whatsapp';
import {
  fetchAtas,
  fetchProdutoCategorias,
  fetchProdutos,
  type Ata,
  type Produto,
  type ProdutoCategoria,
} from '@/services/catalog';

export default function ProdutoDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [products, setProducts] = useState<Produto[]>([]);
  const [atasData, setAtasData] = useState<Ata[]>([]);
  const [categoriesData, setCategoriesData] = useState<ProdutoCategoria[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [selectedVariantId, setSelectedVariantId] = useState<number | null>(null);
  const product = products.find((item) => item.id === Number(id) && item.ativo);
  const activeVariants = useMemo(() => product?.variantes.filter((variant) => variant.ativo) || [], [product]);

  useEffect(() => {
    Promise.all([fetchProdutos(), fetchAtas(), fetchProdutoCategorias()]).then(([productsResult, atasResult, categoriesResult]) => {
      setProducts(productsResult);
      setAtasData(atasResult);
      setCategoriesData(categoriesResult);
      setLoading(false);
    }).catch(() => {
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    if (activeVariants.length > 0 && selectedVariantId === null) {
      setSelectedVariantId(activeVariants[0].id);
    }
  }, [activeVariants, selectedVariantId]);

  if (loading) {
    return (
      <main className="min-h-screen bg-gray-50">
        <Navbar />
        <section className="px-4 pt-32 pb-20 text-center">
          <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-donnos-green border-t-transparent"></div>
        </section>
        <Footer />
      </main>
    );
  }

  if (!product) {
    return (
      <main className="min-h-screen bg-gray-50">
        <Navbar />
        <section className="px-4 pt-32 pb-20 text-center">
          <div className="mx-auto max-w-md rounded-2xl border border-gray-100 bg-white p-8 shadow-sm">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-gray-100">
              <i className="ri-search-line text-2xl text-gray-400"></i>
            </div>
            <h1 className="mb-2 text-xl font-extrabold text-gray-950">Produto não encontrado</h1>
            <p className="mb-5 text-sm text-gray-500">Esse produto pode estar indisponível ou ter sido removido do catálogo.</p>
            <Link
              to="/produtos"
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-donnos-green px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-donnos-green-dark"
            >
              Voltar aos produtos
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>
        <Footer />
      </main>
    );
  }

  const photos = product.fotos.length > 0 ? product.fotos : [{ id: 0, url: '', principal: true }];
  const selectedVariant = activeVariants.find((variant) => variant.id === selectedVariantId) || activeVariants[0];
  const getVariantLabel = (variant: typeof selectedVariant) => {
    if (!variant) return '';
    const variantType = (variant as typeof variant & { tipo?: string }).tipo;
    return [variantType ? `${variantType}:` : '', variant.cor, variant.tamanho].filter(Boolean).join(' ');
  };
  const categories = product.categorias
    .map((categoryId) => categoriesData.find((category) => category.id === categoryId)?.nome)
    .filter(Boolean);
  const atas = product.atasIds
    .map((ataId) => atasData.find((ata) => ata.id === ataId))
    .filter(Boolean);

  const changeQty = (delta: number) => {
    setQuantity((prev) => Math.max(1, Math.min(999, prev + delta)));
  };

  const requestQuote = () => {
    openProductQuoteOnWhatsApp({
      productName: product.nome,
      productSku: product.sku,
      brand: product.marca,
      variant: selectedVariant,
      quantity,
      atasNomes: atas.map((ata) => ata!.numero),
    });
  };

  return (
    <main className="min-h-screen bg-gray-50">
      <Navbar />

      <section className="bg-donnos-dark px-4 pt-28 pb-8 sm:px-6 md:px-8 lg:px-12">
        <div className="mx-auto max-w-7xl">
          <button
            type="button"
            onClick={() => navigate('/produtos')}
            className="mb-5 inline-flex items-center gap-2 text-sm font-semibold text-white/70 transition-colors hover:text-white"
          >
            <i className="ri-arrow-left-line"></i>
            Voltar ao catálogo
          </button>
          <div className="flex flex-wrap items-center gap-2">
            {categories.map((category) => (
              <span key={category} className="rounded-full bg-donnos-green/10 px-3 py-1 text-xs font-bold uppercase tracking-wider text-donnos-green">
                {category}
              </span>
            ))}
          </div>
          <h1 className="mt-3 max-w-4xl text-2xl font-extrabold leading-tight text-white sm:text-3xl md:text-4xl">
            {product.nome}
          </h1>
          <p className="mt-2 text-sm text-gray-400">
            {product.marca} • SKU {product.sku}
          </p>
        </div>
      </section>

      <section className="px-4 py-8 sm:px-6 md:px-8 lg:px-12">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1.1fr)_minmax(360px,0.9fr)]">
          <div className="space-y-4">
            <div className="flex aspect-square items-center justify-center overflow-hidden rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
              {photos[selectedPhotoIndex]?.url ? (
                <img
                  src={photos[selectedPhotoIndex].url}
                  alt={product.nome}
                  className="h-full w-full object-contain"
                />
              ) : (
                <i className="ri-image-line text-5xl text-gray-300"></i>
              )}
            </div>

            {photos.length > 1 && (
              <div className="grid grid-cols-4 gap-3 sm:grid-cols-6">
                {photos.map((photo, index) => (
                  <button
                    key={photo.id}
                    type="button"
                    onClick={() => setSelectedPhotoIndex(index)}
                    className={`aspect-square overflow-hidden rounded-xl border bg-white p-2 transition-all ${
                      selectedPhotoIndex === index
                        ? 'border-donnos-green ring-2 ring-donnos-green/20'
                        : 'border-gray-100 hover:border-donnos-green/50'
                    }`}
                    aria-label={`Ver foto ${index + 1}`}
                  >
                    <img src={photo.url} alt={`${product.nome} ${index + 1}`} className="h-full w-full object-contain" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-5">
            <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm sm:p-6">
              <div className="mb-5 grid grid-cols-3 gap-3">
                <div className="rounded-xl bg-gray-50 p-3">
                  <span className="block text-[10px] font-bold uppercase tracking-wider text-gray-400">Marca</span>
                  <strong className="mt-1 block text-sm text-gray-950">{product.marca}</strong>
                </div>
                <div className="rounded-xl bg-gray-50 p-3">
                  <span className="block text-[10px] font-bold uppercase tracking-wider text-gray-400">Estoque</span>
                  <strong className="mt-1 block text-sm text-donnos-green">{product.estoque}</strong>
                </div>
                <div className="rounded-xl bg-gray-50 p-3">
                  <span className="block text-[10px] font-bold uppercase tracking-wider text-gray-400">Atas</span>
                  <strong className="mt-1 block text-sm text-gray-950">{atas.length}</strong>
                </div>
              </div>

              {activeVariants.length > 0 && (
                <div className="mb-5">
                  <h2 className="mb-3 text-sm font-extrabold text-gray-950">Variantes disponíveis</h2>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {activeVariants.map((variant) => (
                      <button
                        key={variant.id}
                        type="button"
                        onClick={() => setSelectedVariantId(variant.id)}
                        className={`rounded-xl border p-3 text-left transition-all ${
                          selectedVariant?.id === variant.id
                            ? 'border-donnos-green bg-donnos-green/5 ring-2 ring-donnos-green/15'
                            : 'border-gray-100 hover:border-donnos-green/50'
                        }`}
                      >
                        <span className="block text-sm font-bold text-gray-950">
                          {getVariantLabel(variant)}
                        </span>
                        <span className="mt-1 block text-xs text-gray-500">{variant.sku}</span>
                        <span className="mt-2 inline-flex rounded-full bg-donnos-green/10 px-2 py-0.5 text-[10px] font-bold text-donnos-green">
                          {variant.estoque} em estoque
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="mb-5">
                <h2 className="mb-3 text-sm font-extrabold text-gray-950">Quantidade</h2>
                <div className="flex w-full max-w-xs items-center rounded-xl border border-gray-200">
                  <button
                    type="button"
                    onClick={() => changeQty(-1)}
                    disabled={quantity <= 1}
                    className="h-11 flex-1 text-gray-500 transition-colors hover:bg-gray-50 hover:text-donnos-green disabled:cursor-not-allowed disabled:opacity-30"
                    aria-label="Diminuir quantidade"
                  >
                    <i className="ri-subtract-line"></i>
                  </button>
                  <input
                    type="text"
                    value={quantity}
                    readOnly
                    className="h-11 w-16 border-x border-gray-200 bg-transparent text-center text-sm font-bold text-gray-950 outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => changeQty(1)}
                    className="h-11 flex-1 text-gray-500 transition-colors hover:bg-gray-50 hover:text-donnos-green"
                    aria-label="Aumentar quantidade"
                  >
                    <i className="ri-add-line"></i>
                  </button>
                </div>
              </div>

              <button
                type="button"
                onClick={requestQuote}
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-donnos-green px-5 py-3 text-sm font-extrabold text-white transition-colors hover:bg-donnos-green-dark"
              >
                <i className="ri-whatsapp-line text-lg"></i>
                Consultar orçamento pelo WhatsApp
              </button>
            </div>

            {atas.length > 0 && (
              <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm sm:p-6">
                <h2 className="mb-3 text-sm font-extrabold text-gray-950">Atas vinculadas</h2>
                <div className="flex flex-wrap gap-2">
                  {atas.map((ata) => (
                    <span key={ata!.id} className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-bold text-amber-700">
                      {ata!.numero}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="mx-auto mt-8 max-w-7xl rounded-2xl border border-gray-100 bg-white p-5 shadow-sm sm:p-6">
          <h2 className="mb-4 text-lg font-extrabold text-gray-950">Descrição do produto</h2>
          <div
            className="max-w-none text-sm leading-7 text-gray-600 [&_h3]:mb-3 [&_h3]:text-lg [&_h3]:font-extrabold [&_h3]:text-gray-950 [&_li]:ml-5 [&_li]:list-disc [&_p]:mb-3 [&_ul]:space-y-1"
            dangerouslySetInnerHTML={{ __html: product.descricao }}
          />
        </div>
      </section>

      <Footer />
    </main>
  );
}
